import React from "react";
import { Crown, ExternalLink } from "lucide-react";
import { Subscription } from "../../../hooks/useSubscription";

interface SubscriptionSettingsProps {
  isPro: boolean;
  plan: "free" | "monthly" | "yearly" | "included";
  subscription: Subscription | null;
  onUpgradeClick: () => void;
  onManageSubscription: () => void;
}

const planLabels: Record<string, string> = {
  free: "No plan",
  included: "Included",
  monthly: "Monthly",
  yearly: "Yearly",
};

const SubscriptionSettings: React.FC<SubscriptionSettingsProps> = ({
  isPro,
  plan,
  subscription,
  onUpgradeClick,
  onManageSubscription,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-warm-text-primary mb-1">
          Subscription
        </h3>
        <p className="text-sm text-warm-text-secondary">
          Manage your JuDI subscription plan.
        </p>
      </div>

      {/* Current plan */}
      <div className="p-4 rounded-lg border border-warm-border bg-warm-bg-elevated">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                isPro
                  ? "bg-amber-100 dark:bg-amber-900/30"
                  : "bg-warm-bg-elevated"
              }`}
            >
              <Crown
                size={20}
                className={
                  isPro
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-warm-text-tertiary"
                }
              />
            </div>
            <div>
              <div className="font-medium text-warm-text-primary">
                {planLabels[plan] || "Free"}
              </div>
              {isPro && subscription?.cancel_at_period_end && (
                <div className="text-xs text-amber-600 dark:text-amber-400">
                  Cancels at end of period
                </div>
              )}
              {isPro && subscription?.current_period_end && (
                <div className="text-xs text-warm-text-tertiary">
                  {subscription.cancel_at_period_end ? "Access until" : "Renews"}{" "}
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </div>
              )}
              {plan === "included" && (
                <div className="text-xs text-warm-text-tertiary">
                  This account does not pay
                </div>
              )}
              {!isPro && (
                <div className="text-xs text-warm-text-tertiary">
                  1 month free, then $5 a month
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isPro && plan !== "included" ? (
        <button
          onClick={onManageSubscription}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-lg transition-colors"
        >
          <ExternalLink size={16} />
          Manage Subscription
        </button>
      ) : null}
      {!isPro ? (
        <button
          onClick={onUpgradeClick}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-warm-blue hover:bg-warm-blue-dark rounded-lg transition-colors"
        >
          <Crown size={16} />
          Subscribe
        </button>
      ) : null}
    </div>
  );
};

export default SubscriptionSettings;
